
/*********************************************************************
 *
 * WARNING: This shader class is automatically generated, any manual
 * edits will be lost.
 *
 ********************************************************************/
import { ShaderBase, ShaderUniformsContainer, ShaderAttributesContainer } from '../shader';
import { Texture, CubeMapTexture } from '../../texture';
import { Context } from '../../context/Context';
import { mat3, mat4, vec2, vec3, vec4 } from "gl-matrix";
import { ShaderSourceParser } from '../shader-source-parser';

export type GBufferShaderUniforms = {
	uProjectionMatrix: WebGLUniformLocation,
	uUnjitteredViewProjection: WebGLUniformLocation,
	uPreviousViewProjection: WebGLUniformLocation,
	uViewMatrix: WebGLUniformLocation,
	uModelViewMatrix: WebGLUniformLocation,
	uModelMatrix: WebGLUniformLocation,
	uPresetColor: WebGLUniformLocation,
	uBindShapeMatrix: WebGLUniformLocation,
	uMatrixSampler: WebGLUniformLocation,
	uDataSettings: WebGLUniformLocation,
	uBlendSettings: WebGLUniformLocation,
	uBoneOverrides: WebGLUniformLocation[],
	uBoneOverrideIndices: WebGLUniformLocation[],
	uBoneOverrideCount: WebGLUniformLocation,
	uBoneMatrix: WebGLUniformLocation,
	uTextureSampler: WebGLUniformLocation,
	uNormalMapSampler: WebGLUniformLocation,
	uMetallicRoughnessSampler: WebGLUniformLocation,
	uImpostorHeightBounds: WebGLUniformLocation,
	uDitherFade: WebGLUniformLocation,
	uDitherCellSize: WebGLUniformLocation
};

export type GBufferShaderAttributes = {
	aInstanceModelCol0: number,
	aInstanceModelCol1: number,
	aInstanceModelCol2: number,
	aInstanceModelCol3: number,
	aVertexPosition: number,
	aVertexNormal: number,
	aTextureCoord: number,
	aVertexColor: number,
	aJointWeights: number,
	aJointIndices: number,
	vWorldPosition: number,
	vWorldNormal: number,
	vClipCurr: number,
	vClipPrev: number,
	vTextureCoord: number,
	vColor: number
};

export type GBufferShaderSettings = ('INSTANCED' | 'TEXTURE' | 'ATTRIBUTE_COLOR' | 'PRESET_COLOR' | 'ANIMATION' | 'BONE_OVERRIDES' | 'ATTACHMENT' | 'NORMAL_MAP' | 'METALLIC_ROUGHNESS' | 'IMPOSTOR_AO' | 'CROSSFADE' | 'IMPOSTOR_OUTPUT')[];

export class GBufferShader extends ShaderBase<GBufferShaderUniforms, GBufferShaderAttributes> {

    private static fragmentShaderSource = `
layout(location = 0) out vec4 gAlbedo;
layout(location = 1) out vec4 gNormal;
layout(location = 2) out vec2 gMotion;

in vec4 vWorldPosition;
in vec3 vWorldNormal;
in vec4 vClipCurr;
in vec4 vClipPrev;

#ifdef NOODLE_TEXTURE
in vec2 vTextureCoord;
uniform sampler2D uTextureSampler;
#endif

#ifdef NOODLE_ATTRIBUTE_COLOR
in vec4 vColor;
#endif

#ifdef NOODLE_PRESET_COLOR
uniform vec4 uPresetColor;
#endif

#ifdef NOODLE_NORMAL_MAP
// --- begin: normal_map.glsl ---
uniform sampler2D uNormalMapSampler;

vec3 perturb_normal(vec3 N, vec3 worldPos, vec2 texCoord) {
    vec3 dp1 = dFdx(worldPos);
    vec3 dp2 = dFdy(worldPos);
    vec2 duv1 = dFdx(texCoord);
    vec2 duv2 = dFdy(texCoord);

    vec3 dp2perp = cross(dp2, N);
    vec3 dp1perp = cross(N, dp1);

    vec3 T = dp2perp * duv1.x + dp1perp * duv2.x;
    vec3 B = dp2perp * duv1.y + dp1perp * duv2.y;

    float invmax = inversesqrt(max(dot(T, T), dot(B, B)));
    mat3 TBN = mat3(T * invmax, B * invmax, N);

    vec3 mapN = texture(uNormalMapSampler, texCoord).rgb * 2.0 - 1.0;
    return normalize(TBN * mapN);
}

// --- end: normal_map.glsl ---
#endif

#ifdef NOODLE_METALLIC_ROUGHNESS
uniform sampler2D uMetallicRoughnessSampler;
#endif

#ifdef NOODLE_IMPOSTOR_AO
// x = localMin.y, y = localMax.y of the captured mesh
uniform vec2 uImpostorHeightBounds;
#endif

#ifdef NOODLE_CROSSFADE
uniform float uDitherFade;
// Cell size in framebuffer pixels. Defaults to 1 (single-pixel checker — what
// LOD crossfade uses, since it needs the smoothest possible transition).
// Larger values produce visible chunky stipple that survives TAA / FXAA;
// the editor's ghost-render path uses ~4 to mark scatter previews.
uniform float uDitherCellSize;
const float bayer4x4[16] = float[](
	 0.0/16.0,  8.0/16.0,  2.0/16.0, 10.0/16.0,
	12.0/16.0,  4.0/16.0, 14.0/16.0,  6.0/16.0,
	 3.0/16.0, 11.0/16.0,  1.0/16.0,  9.0/16.0,
	15.0/16.0,  7.0/16.0, 13.0/16.0,  5.0/16.0
);
#endif

void main(void) {
#ifdef NOODLE_CROSSFADE
	int cell = max(int(uDitherCellSize), 1);
	ivec2 pc = ivec2(gl_FragCoord.xy) / cell;
	float ditherThreshold = bayer4x4[(pc.x & 3) * 4 + (pc.y & 3)];
	// Positive uDitherFade: fade OUT (discard low bayer values → progressively transparent)
	// Negative uDitherFade: fade IN  (discard high bayer values → complementary pattern)
	if (uDitherFade >= 0.0) {
		if (ditherThreshold < uDitherFade) discard;
	} else {
		if (ditherThreshold >= -uDitherFade) discard;
	}
#endif

	vec4 albedo = vec4(1.0);

#ifdef NOODLE_TEXTURE
	albedo = texture(uTextureSampler, vTextureCoord);
	if (albedo.a < 0.5) discard;
#endif

#ifdef NOODLE_ATTRIBUTE_COLOR
	albedo = vColor;
#else
#ifdef NOODLE_PRESET_COLOR
	albedo = uPresetColor;
#endif
#endif

#ifdef NOODLE_TEXTURE
#ifdef NOODLE_PRESET_COLOR
	albedo.rgb *= uPresetColor.rgb;
#endif
#endif

	vec3 N = normalize(vWorldNormal);
#ifdef NOODLE_NORMAL_MAP
	N = perturb_normal(N, vWorldPosition.xyz, vTextureCoord);
#endif

	// PBR metallic-roughness is packed into the .a channels of the albedo and normal
	// buffers to avoid a 3rd render target. The deferred lighting pass reads them back.
	// glTF packs: green channel = roughness, blue channel = metallic.
	float roughness = 0.5;
	float metallic = 0.0;
#ifdef NOODLE_METALLIC_ROUGHNESS
	vec4 mr = texture(uMetallicRoughnessSampler, vTextureCoord);
	roughness = mr.g;
	metallic = mr.b;
#endif

	// Metallic packs into albedo.a (8-bit, industry-standard PBR precision).
	// Geometry-vs-background detection moves to the depth buffer (reversed-Z cleared depth = 0.0).
	// IMPOSTOR_OUTPUT mode replaces the metallic pack with a coverage flag (1.0)
	// so the impostor texture's alpha can drive the billboard's alpha discard.
#ifdef NOODLE_IMPOSTOR_OUTPUT
#ifdef NOODLE_IMPOSTOR_AO
	// Height-based AO: bottom of a grounded object sits near the ground and other
	// geometry, so SSAO darkens it at runtime; the top is exposed and stays bright.
	// Use a world-space falloff (meters below the top) so a 1m rock gets only a
	// gentle gradient while a 5m tree gets the full effect. AO_REACH is roughly
	// the depth at which occlusion saturates — tuned for typical scatter assets.
	const float AO_REACH = 3.5;
	float worldDepth = uImpostorHeightBounds.y - vWorldPosition.y;
	float aoFrac = clamp(worldDepth / AO_REACH, 0.0, 1.0);
	albedo.rgb *= mix(1.0, 0.4, aoFrac);
#endif
	gAlbedo = vec4(albedo.rgb, 1.0);
#else
	gAlbedo = vec4(albedo.rgb, metallic);
#endif
	gNormal = vec4(N, roughness);

	// Screen-space motion vector: prevUV - currentUV. TAA reads this to find
	// where this pixel was last frame.
	vec2 ndcCurr = vClipCurr.xy / vClipCurr.w;
	vec2 ndcPrev = vClipPrev.xy / vClipPrev.w;
	gMotion = (ndcPrev - ndcCurr) * 0.5;
}
`;

    private static vertexShaderSource = `
uniform mat4 uProjectionMatrix;
// Unjittered VP and previous-frame VP — used to derive screen-space motion vectors
// without picking up TAA's sub-pixel projection jitter (jitter on motion vectors
// would oscillate every pixel by the jitter offset, ruining reprojection).
uniform mat4 uUnjitteredViewProjection;
uniform mat4 uPreviousViewProjection;

#ifdef NOODLE_INSTANCED
layout(location = 6) in vec4 aInstanceModelCol0;
layout(location = 7) in vec4 aInstanceModelCol1;
layout(location = 8) in vec4 aInstanceModelCol2;
layout(location = 9) in vec4 aInstanceModelCol3;
uniform mat4 uViewMatrix;
#else
uniform mat4 uModelViewMatrix;
uniform mat4 uModelMatrix;
#endif

layout(location = 0) in vec3 aVertexPosition;
out vec4 vWorldPosition;
out vec3 vWorldNormal;
out vec4 vClipCurr;
out vec4 vClipPrev;

layout(location = 1) in vec3 aVertexNormal;

#ifdef NOODLE_TEXTURE
layout(location = 2) in vec2 aTextureCoord;
out vec2 vTextureCoord;
#endif

#ifdef NOODLE_ATTRIBUTE_COLOR
layout(location = 3) in vec4 aVertexColor;
out vec4 vColor;
#endif

#ifdef NOODLE_PRESET_COLOR
uniform vec4 uPresetColor;
#endif

#ifdef NOODLE_ANIMATION
// --- begin: animation.glsl ---
layout(location = 4) in vec4 aJointWeights;
layout(location = 5) in vec4 aJointIndices;

uniform mat4 uBindShapeMatrix;
uniform sampler2D uMatrixSampler;
uniform vec3 uDataSettings;
uniform vec4 uBlendSettings;

#ifdef NOODLE_BONE_OVERRIDES
uniform mat4 uBoneOverrides[32];
uniform int uBoneOverrideIndices[32];
uniform int uBoneOverrideCount;
#endif

mat4 read_matrix(sampler2D tex, float i, vec2 size) {
	const float offset = 0.5;
	float tx = mod(i * 4.0, size.x) + offset;
	float ty = floor(i * 4.0 / size.x) + offset;

	return mat4(
		texture(tex, vec2((tx + 0.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 1.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 2.0) / size.x, ty / size.y)),
		texture(tex, vec2((tx + 3.0) / size.x, ty / size.y))
	);
}

mat4 read_animation() {
	mat4 matrix = mat4(0.0);
	for (int j = 0; j < 4; j++) {
		float w = aJointWeights[j];
		if (w > 0.0) {
			float jointIndex = float(aJointIndices[j]);
			float index = jointIndex + uDataSettings.z;
			mat4 jointMatrix = read_matrix(uMatrixSampler, index, uDataSettings.xy);

			// Blend with previous clip if active
			if (uBlendSettings.w > 0.0) {
				mat4 prevMatrix = read_matrix(uMatrixSampler, jointIndex + uBlendSettings.z, uBlendSettings.xy);
				jointMatrix = prevMatrix + (jointMatrix - prevMatrix) * uBlendSettings.w;
			}

			#ifdef NOODLE_BONE_OVERRIDES
			for (int k = 0; k < uBoneOverrideCount; k++) {
				if (uBoneOverrideIndices[k] == int(jointIndex)) {
					jointMatrix = uBoneOverrides[k];
					break;
				}
			}
			#endif

			matrix += jointMatrix * w;
		}
	}
	return matrix;
}

// --- end: animation.glsl ---
#endif

#ifdef NOODLE_ATTACHMENT
uniform mat4 uBoneMatrix;
#endif

void main(void) {

#ifdef NOODLE_ANIMATION
	mat4 animationMatrix = read_animation();
	vec4 pos = vec4(aVertexPosition, 1.0) * uBindShapeMatrix * animationMatrix;
	vec4 normal = vec4(aVertexNormal, 0.0) * uBindShapeMatrix * animationMatrix;
#else
	vec4 pos = vec4(aVertexPosition, 1.0);
	vec4 normal = vec4(aVertexNormal, 0.0);
#endif

#ifdef NOODLE_ATTACHMENT
	pos = pos * uBoneMatrix;
	normal = normal * uBoneMatrix;
#endif

#ifdef NOODLE_ATTRIBUTE_COLOR
#ifdef NOODLE_PRESET_COLOR
	vColor = aVertexColor * uPresetColor;
#else
	vColor = aVertexColor;
#endif
#endif

#ifdef NOODLE_TEXTURE
	vTextureCoord = aTextureCoord;
#endif

#ifdef NOODLE_INSTANCED
	mat4 instanceModel = mat4(aInstanceModelCol0, aInstanceModelCol1, aInstanceModelCol2, aInstanceModelCol3);
	vWorldPosition = instanceModel * vec4(pos.xyz, 1.0);
	vWorldNormal = normalize(mat3(instanceModel) * normal.xyz);
	gl_Position = uProjectionMatrix * uViewMatrix * vWorldPosition;
#else
	vWorldPosition = uModelMatrix * vec4(pos.xyz, 1.0);
	vWorldNormal = normalize(mat3(uModelMatrix) * normal.xyz);
	vec4 viewPos = uModelViewMatrix * vec4(pos.xyz, 1.0);
	gl_Position = uProjectionMatrix * viewPos;
#endif

	// Motion vector inputs: clip-space positions of this same world vertex under
	// (a) the unjittered current VP and (b) the previous-frame unjittered VP.
	// Animated/dynamic objects reuse current world position for the previous frame
	// — that captures camera-induced motion only. Per-object motion is a follow-on.
	vClipCurr = uUnjitteredViewProjection * vWorldPosition;
	vClipPrev = uPreviousViewProjection * vWorldPosition;
}
`;

    public override get version(): string {
        return '300 es';
    }

    public override get parser(): any {
        return new ShaderSourceParser();
    }

    protected override get warnAttributeMismatch(): boolean {
        return false;
    }

    public uniformsContainer: GBufferShaderUniformsContainer;
    public attributesContainer: GBufferShaderShaderAttributesContainer;

    constructor(ctx: Context, settings: GBufferShaderSettings) {
        super(ctx, GBufferShader.fragmentShaderSource, GBufferShader.vertexShaderSource, settings);
        this.uniformsContainer = new GBufferShaderUniformsContainer(ctx, this);
        this.attributesContainer = new GBufferShaderShaderAttributesContainer(ctx, this);
    }
}

class GBufferShaderUniformsContainer extends ShaderUniformsContainer {
    constructor(ctx: Context, private shader: GBufferShader) {
        super(ctx);
    }

    public setProjectionMatrix(value: mat4 | number[]) : GBufferShaderUniformsContainer {
        this.setValue('uProjectionMatrix', this.shader.uniforms.uProjectionMatrix, value, 'mat4');
        return this;
    }

    public setUnjitteredViewProjection(value: mat4 | number[]) : GBufferShaderUniformsContainer {
        this.setValue('uUnjitteredViewProjection', this.shader.uniforms.uUnjitteredViewProjection, value, 'mat4');
        return this;
    }

    public setPreviousViewProjection(value: mat4 | number[]) : GBufferShaderUniformsContainer {
        this.setValue('uPreviousViewProjection', this.shader.uniforms.uPreviousViewProjection, value, 'mat4');
        return this;
    }

    public setViewMatrix(value: mat4 | number[]) : GBufferShaderUniformsContainer {
        this.setValue('uViewMatrix', this.shader.uniforms.uViewMatrix, value, 'mat4');
        return this;
    }

    public setModelViewMatrix(value: mat4 | number[]) : GBufferShaderUniformsContainer {
        this.setValue('uModelViewMatrix', this.shader.uniforms.uModelViewMatrix, value, 'mat4');
        return this;
    }

    public setModelMatrix(value: mat4 | number[]) : GBufferShaderUniformsContainer {
        this.setValue('uModelMatrix', this.shader.uniforms.uModelMatrix, value, 'mat4');
        return this;
    }

    public setPresetColor(value: vec4 | number[]) : GBufferShaderUniformsContainer {
        this.setValue('uPresetColor', this.shader.uniforms.uPresetColor, value, 'vec4');
        return this;
    }

    public setBindShapeMatrix(value: mat4 | number[]) : GBufferShaderUniformsContainer {
        this.setValue('uBindShapeMatrix', this.shader.uniforms.uBindShapeMatrix, value, 'mat4');
        return this;
    }

    public setMatrixSampler(texture: Texture) : GBufferShaderUniformsContainer {
        this.setTextureValue('uMatrixSampler', this.shader.uniforms.uMatrixSampler, texture, 'sampler2D');
        return this;
    }

    public setDataSettings(value: vec3 | number[]) : GBufferShaderUniformsContainer {
        this.setValue('uDataSettings', this.shader.uniforms.uDataSettings, value, 'vec3');
        return this;
    }

    public setBlendSettings(value: vec4 | number[]) : GBufferShaderUniformsContainer {
        this.setValue('uBlendSettings', this.shader.uniforms.uBlendSettings, value, 'vec4');
        return this;
    }

    public setBoneOverrides(index: number, value: mat4 | number[]) : GBufferShaderUniformsContainer {
        if(index < 0 || index >= 32) throw new Error('Invalid index used for uBoneOverrides: ' + index);
        this.setValue('uBoneOverrides', this.shader.uniforms.uBoneOverrides[index], value, 'mat4');
        return this;
    }

    public setBoneOverrideIndices(index: number, value: number) : GBufferShaderUniformsContainer {
        if(index < 0 || index >= 32) throw new Error('Invalid index used for uBoneOverrideIndices: ' + index);
        this.setValue('uBoneOverrideIndices', this.shader.uniforms.uBoneOverrideIndices[index], value, 'int');
        return this;
    }

    public setBoneOverrideCount(value: number) : GBufferShaderUniformsContainer {
        this.setValue('uBoneOverrideCount', this.shader.uniforms.uBoneOverrideCount, value, 'int');
        return this;
    }

    public setBoneMatrix(value: mat4 | number[]) : GBufferShaderUniformsContainer {
        this.setValue('uBoneMatrix', this.shader.uniforms.uBoneMatrix, value, 'mat4');
        return this;
    }

    public setTextureSampler(texture: Texture) : GBufferShaderUniformsContainer {
        this.setTextureValue('uTextureSampler', this.shader.uniforms.uTextureSampler, texture, 'sampler2D');
        return this;
    }

    public setNormalMapSampler(texture: Texture) : GBufferShaderUniformsContainer {
        this.setTextureValue('uNormalMapSampler', this.shader.uniforms.uNormalMapSampler, texture, 'sampler2D');
        return this;
    }

    public setMetallicRoughnessSampler(texture: Texture) : GBufferShaderUniformsContainer {
        this.setTextureValue('uMetallicRoughnessSampler', this.shader.uniforms.uMetallicRoughnessSampler, texture, 'sampler2D');
        return this;
    }

    public setImpostorHeightBounds(value: vec2 | number[]) : GBufferShaderUniformsContainer {
        this.setValue('uImpostorHeightBounds', this.shader.uniforms.uImpostorHeightBounds, value, 'vec2');
        return this;
    }

    public setDitherFade(value: number) : GBufferShaderUniformsContainer {
        this.setValue('uDitherFade', this.shader.uniforms.uDitherFade, value, 'float');
        return this;
    }

    public setDitherCellSize(value: number) : GBufferShaderUniformsContainer {
        this.setValue('uDitherCellSize', this.shader.uniforms.uDitherCellSize, value, 'float');
        return this;
    }

    protected getTextureUniforms() {
        return [
			{ name: 'uMatrixSampler', uniform: this.shader.uniforms.uMatrixSampler, dataType: 'sampler2D' },
			{ name: 'uTextureSampler', uniform: this.shader.uniforms.uTextureSampler, dataType: 'sampler2D' },
			{ name: 'uNormalMapSampler', uniform: this.shader.uniforms.uNormalMapSampler, dataType: 'sampler2D' },
			{ name: 'uMetallicRoughnessSampler', uniform: this.shader.uniforms.uMetallicRoughnessSampler, dataType: 'sampler2D' }
        ];
    }
}

class GBufferShaderShaderAttributesContainer extends ShaderAttributesContainer {
    constructor(ctx: Context, private shader: GBufferShader) {
        super(ctx);
    }

    public setInstanceModelCol0(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol0, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setInstanceModelCol1(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol1, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setInstanceModelCol2(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol2, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setInstanceModelCol3(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aInstanceModelCol3, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setVertexPosition(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setVertexNormal(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexNormal, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setTextureCoord(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aTextureCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setVertexColor(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aVertexColor, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setJointWeights(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aJointWeights, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setJointIndices(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.aJointIndices, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldPosition(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldPosition, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvWorldNormal(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vWorldNormal, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvClipCurr(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vClipCurr, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvClipPrev(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vClipPrev, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvTextureCoord(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vTextureCoord, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }

    public setvColor(settings: { length: number, offset: number, itemSize: number }) : GBufferShaderShaderAttributesContainer {
        const gl = this.gl;
        this.bindFloatAttribute(this.shader.attributes.vColor, settings.length, settings.offset, settings.itemSize, gl.FLOAT, Float32Array.BYTES_PER_ELEMENT);
        return this;
    }
}